/**
 * AdminDashboardTotals/Functions.js
 *
 * 2026-09-04 — fully isolated clone of the data-fetching functions
 * InitialDashBoard/Functions.js uses for the admin dashboard's "Totals" tab.
 * Built after confirming (client-reported, Altitude): the Totals tab showed
 * Mayaland's own revenue AND Mayaland's own salespeople, because
 * getSalesPerRoute/getSalesPerSalesPerson/getSalesByCustomer all called
 * /invoice/get/Total/byRouteV2, whose backend (alt_controllers/invoice.ts ->
 * QuerysMAS90.ts's getTotalSalesByRouteV2/getGeneralTotalSalesByRouteV2)
 * hardcodes mas90_mirror__AR_InvoiceHistoryHeader/AR_Customer (no alt__
 * prefix, no idCompany param at all).
 *
 * InitialDashBoard/Functions.js is shared with SalesLobbyV2.js (the
 * individual-salesperson dashboard another developer is actively fixing in
 * parallel) — editing it risked breaking the individual tabs (which the
 * client confirmed DO work) and colliding with that concurrent work. This
 * file is a clean copy, calling the new idCompany-aware
 * /v3/sales/total-by-route (api-portal-manager's salesReports.ts) instead of
 * the old endpoint for the 3 affected functions. Every other function here
 * was verified (2026-09-04) to already be idCompany-aware server-side
 * (getCustomerBalanceV2, carteraClientesExcesoV2, HistoryV2,
 * getSalesByRouteYear all correctly branch on alt__ vs plain prefix) — kept
 * calling the SAME existing endpoints, unchanged, nothing to fix there.
 *
 * Mayaland's own Totals tab (AdminLobbyV2.js + InitialDashBoard/Functions.js)
 * is untouched by this file entirely — nothing here is imported by it.
 */

import { SalesByCustomerRoute, SalesPerMonth, SalesPerWeek } from '../AdminDashboardTotals/components/TableDetailSalesPerRoute/Class';
import { ActualDate, DateFormatMAS90, getActualDateUTC, getWeekNumber } from '../../functions/dateFormat';
import { intervalTimeInDays, intervalTimeInDays2, MonthInLetters, NumberFormat, random_rgba } from '../../functions/generalFunctions';
import { pricesFormat } from '../../functions/pricesFormat';
import { getIndexElement } from '../../functions/searchInObject';
import { getInformationWithDataDashboard as getInformationWithData, getInformationWithDataV3 } from '../../services/CABE';
import { getValueCookie } from '../../services/cookieService';
import { GeneralFormatToSend, SalesPerRouteMonth, TitlesSalesPerRoutes } from '../InitialDashBoard/Class';
import React from 'react';

function currentIdCompany() {
    return getValueCookie('SubCompanyId') ? Number(getValueCookie('SubCompanyId')) : Number(getValueCookie('CompanyId'));
}

export async function getSalesOrdersPerUser(SalesPerson) {
    const data = {
        SalespersonNo: SalesPerson ? SalesPerson : getValueCookie('SalesPerson'),
        idCompany: currentIdCompany(),
        CustomerNo: '*'
    }
    const SalesOrder = await getInformationWithData('/sales/get/headerV2', data);
    const Invoices = await getInformationWithData('/invoice/headerV2', data);

    if (SalesOrder.status.code === 1) {
        for (const order of SalesOrder.data) {
            order.ColorRow = intervalTimeInDays(order.OrderDate, ActualDate()) >= 7 ? "grayTableCell" : (intervalTimeInDays(order.OrderDate, ActualDate()) > 5 ? "yellowTableCell" : "greenTableCell")
            order.OrderDate = DateFormatMAS90(order.OrderDate)
            order.TypeinLetters = order.OrderType === 'S' ? "Standard" : "Quote"
            order.ExistInv = getIndexElement(Invoices.data, 'SalesOrderNo', order.SalesOrderNo) === -1 ? 'No' : 'Yes'
            order.DaysSystem = intervalTimeInDays(order.OrderDate, ActualDate())
            order.StatusColor = ((!order.Status) || (order.Status === 0)) ? "text-danger" : (order.Status === 1 ? "text-warning" : "text-success")
            order.StatusText = ((!order.Status) || (order.Status === 0)) ? "Not started" : (order.Status === 1 ? "In process" : "Finished")
        }
        return SalesOrder.data;
    } else {
        return []
    }
}

/**
 * getTotalSalesByRoute (2026-09-04) — the shared building block for
 * getSalesPerRoute/getSalesPerSalesPerson/getSalesByCustomer below. Calls
 * the new idCompany-aware v3 endpoint instead of the old broken
 * /invoice/get/Total/byRouteV2.
 *
 * Switched to the '-txn' (TransactionDate-grouped) variant (2026-09-09,
 * client-reported — Dashboard Totals didn't match MAS90's own monthly sales
 * reports). Root cause: this used to filter/group by InvoiceDate, but MAS90's
 * own reports group by TransactionDate (actual GL posting date) — a batch of
 * end-of-month invoices routinely posts the next day, shifting real dollars
 * across the month boundary. See salesReports.ts's actualMonthTxn header
 * comment (api-portal-manager) for the full root-cause writeup and live
 * verification (Altitude, Jan-Aug 2026, closes to within $0.01-0.04 of
 * MAS90's own figures). Every consumer below now reads row.TransactionDate
 * (not row.InvoiceDate) when bucketing by month/day for this same reason.
 */
async function fetchTotalSalesByRoute(SalesPerson, Start, End) {
    return getInformationWithDataV3('/v3/sales/total-by-route-txn', {
        idCompany: currentIdCompany(),
        SalespersonNo: SalesPerson ? SalesPerson : getValueCookie('SalesPerson'),
        Start: Start.toISOString().split('T')[0],
        End: End.toISOString().split('T')[0],
    });
}

export async function getSalesPerRoute(SalesPerson, DateC) {
    let colorsRoute = [
        { Ruta: 'Warehouse',   Color: '#004C94' },
        { Ruta: 'Supermarket', Color: '#73CC80' },
        { Ruta: 'Distributor', Color: '#3C93FA' },
        { Ruta: 'Pallet',      Color: '#028183' }
    ];
    let temp = new Date();
    let Start = new Date(temp.getFullYear(), temp.getMonth(), 1);
    let End = new Date(temp.getFullYear(), temp.getMonth() + 1, 0);

    if (DateC) {
        Start = new Date(DateC.getFullYear(), DateC.getMonth(), 1);
        End = new Date(DateC.getFullYear(), DateC.getMonth() + 1, 0);
    }

    const data = new GeneralFormatToSend(getValueCookie('CompanyId'), SalesPerson ? SalesPerson : getValueCookie('SalesPerson'), Start.toISOString().split('T')[0], End.toISOString().split('T')[0], (Start.getMonth() + 1))

    const result = await fetchTotalSalesByRoute(SalesPerson, Start, End);
    const result2 = await getInformationWithData('/reports/getMeta', data);

    let TotalMeta = 0;
    let GeneralTotal = 0;
    result.data.forEach(element => {
        GeneralTotal += Number(element.NonTaxableSalesAmt);
    })
    result2.data.forEach(element => {
        TotalMeta += Number(element.Meta);
    });
    let processInfo = [];
    let Months = new TitlesSalesPerRoutes(false, (Start.getMonth() + 1));

    if (result.status.code === 1 && result2.status.code === 1) {
        if (Array.isArray(result.data)) {
            result.data.forEach(row => {
                const indexMeta = getIndexElement(result2.data, 'Ruta', row.UDF_RUTA);
                const index = getIndexElement(processInfo, 'Name', row.UDF_RUTA);
                // TransactionDate, not InvoiceDate (2026-09-09) — see fetchTotalSalesByRoute's header comment above.
                const date = row.TransactionDate ? row.TransactionDate.split('-') : [];

                if (date.length === 3) {
                    const index2 = getIndexElement(Months, 'Month', MonthInLetters(date[1]));
                    let total = 0;
                    if (index2 !== -1) {
                        Months[index2].Total += (Number(row.NonTaxableSalesAmt));
                        Months[index2].TotalMeta = TotalMeta;
                        total = Months[index2].Total;
                    }

                    let to = 0;
                    if (index !== -1) {
                        to = processInfo[index].addData(MonthInLetters(date[1]), row.NonTaxableSalesAmt, indexMeta !== -1 ? result2.data[indexMeta].Meta : 0);
                        processInfo[index].addRow(row);
                    } else {
                        let newRoute = new SalesPerRouteMonth(row.UDF_RUTA, (Start.getMonth() + 1));
                        newRoute.addRow(row);
                        to = newRoute.addData(MonthInLetters(date[1]), row.NonTaxableSalesAmt, indexMeta !== -1 ? result2.data[indexMeta].Meta : 0);
                        processInfo.push(newRoute);
                    }

                    for (const route of processInfo) {
                        const indexMonth = getIndexElement(route.Data, 'Month', MonthInLetters(date[1]));
                        if (indexMonth !== -1 && to > 0) {
                            route.Data[indexMonth].Percentage = Number(total) > 0 ? ((Number(route.Data[indexMonth].Total) / Number(total)) * 100) : 0
                            route.Data[indexMonth].PercentageMeta = TotalMeta > 0 ? ((Number(route.Data[indexMonth].TotalMeta) / TotalMeta) * 100) : 0
                            route.Data[indexMonth].PercentageComplete = Number(route.Data[indexMonth].TotalMeta) > 0 ? ((Number(route.Data[indexMonth].Total)) / (Number(route.Data[indexMonth].TotalMeta))) * 100 : 0
                            if (route.Data[indexMonth].PercentageComplete < 30) {
                                route.Color = 'grayTableCell';
                            } else if (route.Data[indexMonth].PercentageComplete < 99) {
                                route.Color = 'yellowTableCell';
                            } else {
                                route.Color = 'greenTableCell';
                            }
                        }
                    }

                    if (index2 !== -1) {
                        const indexColor = getIndexElement(colorsRoute, 'Ruta', row.UDF_RUTA)
                        if (indexColor !== -1) {
                            Months[index2].addRoute(row.UDF_RUTA, GeneralTotal > 0 ? (to / GeneralTotal) * 100 : 0, row.UDF_RUTA, colorsRoute[indexColor].Color);
                        } else {
                            Months[index2].addRoute(row.UDF_RUTA, GeneralTotal > 0 ? (to / GeneralTotal) * 100 : 0, row.UDF_RUTA, random_rgba());
                        }
                    }
                }
            });
        }
    }

    processInfo = processInfo.sort((a, b) => {
        return a.Data[0].Total < b.Data[0].Total ? 1 : a.Data[0].Total > b.Data[0].Total ? -1 : 0;
    });

    return { Information: processInfo, Months: Months };
}

export async function getSalesPerRouteAllYear() {
    // Switched from the v1/v2 /Invoice/SalesByRouteYearV2 to the new v3
    // '-txn' clone (2026-09-09) — same TransactionDate-vs-InvoiceDate root
    // cause as fetchTotalSalesByRoute above (see its header comment). The old
    // endpoint (alt_controllers/invoice.ts / my_controllers/invoice.ts) is
    // v1/v2, frozen, left untouched — response shape is identical
    // ({Route, Detail: [{MonthNo, Year, Total}]}), so nothing below this line changes.
    const result = await getInformationWithDataV3('/v3/sales/route-year-txn', { idCompany: currentIdCompany() })
    if (result.status.code === 1) {
        const now = new Date()
        const currentMonthKey = now.getFullYear() * 12 + now.getMonth()
        const MONTHS_TO_SHOW = 13
        for (const Warehouse of result.data) {
            Warehouse.Detail = (Warehouse.Detail || [])
                .filter((Line) => {
                    const lineMonthKey = Number(Line.Year) * 12 + (Number(Line.MonthNo) - 1)
                    const monthsAgo = currentMonthKey - lineMonthKey
                    return monthsAgo >= 0 && monthsAgo < MONTHS_TO_SHOW
                })
                .sort((a, b) => (Number(a.Year) * 12 + Number(a.MonthNo)) - (Number(b.Year) * 12 + Number(b.MonthNo)))
        }

        for (const Warehouse of result.data) {
            let Total = 0;
            for (const Line of Warehouse.Detail) {
                Total += Line.Total;
            }
            Warehouse.Total = Total;
        }
        let flag = true;
        for (const Warehouse of result.data) {
            flag = Warehouse.Route === 'Total';

            let labels = [];
            let data = [];
            let data2 = [];
            let colors = [
                'rgba(37,  99,  235, 0.82)', 'rgba(16,  185, 129, 0.82)', 'rgba(245, 158, 11,  0.82)',
                'rgba(139, 92,  246, 0.82)', 'rgba(239, 68,  68,  0.82)', 'rgba(6,   182, 212, 0.82)',
                'rgba(249, 115, 22,  0.82)', 'rgba(20,  184, 166, 0.82)', 'rgba(99,  102, 241, 0.82)',
                'rgba(236, 72,  153, 0.82)', 'rgba(132, 204, 22,  0.82)', 'rgba(234, 179, 8,   0.82)'
            ];
            for (const Line of Warehouse.Detail) {
                labels.push(`${Line.MonthNo}/${Line.Year}`)
                data.push(Line.Total)
                if (flag) {
                    data2.push(Line.Total)
                } else {
                    data2.push((Line.Total / Warehouse.Total) * 100)
                }
            }

            Warehouse.Graph = {
                options: {
                    responsive: true,
                    plugins: {
                        legend: { position: 'top' },
                        title: { display: true, text: 'Sales of ' + Warehouse.Route },
                        tooltip: {
                            callbacks: {
                                label: function (context) {
                                    let label = context.dataset.label || '';
                                    if (label) { label += ': $ '; }
                                    label += NumberFormat(pricesFormat(parseFloat(context.parsed.y.toString().replace(/,/g, '.'))));
                                    return label;
                                },
                            },
                        },
                    },
                    scales: {
                        y: {
                            ticks: {
                                callback: function (value) { return '$' + NumberFormat(Number(value).toFixed(0)); }
                            }
                        }
                    }
                },
                data: { labels: labels, datasets: [{ label: 'Sales', data: data, backgroundColor: colors }] },
                reference: React.createRef()
            }
            let LineColor = 'rgba(37, 99, 235, 0.85)'
            Warehouse.Line = {
                options: {
                    responsive: true,
                    plugins: flag ? {
                        legend: { position: 'top' },
                        title: { display: true, text: 'Sales of ' + Warehouse.Route },
                        tooltip: {
                            callbacks: {
                                label: function (context) {
                                    let label = context.dataset.label || '';
                                    if (label) { label += ': $ '; }
                                    label += NumberFormat(pricesFormat(parseFloat(context.parsed.y.toString().replace(/,/g, '.'))));
                                    return label;
                                },
                            },
                        },
                    } : {
                        legend: { position: 'top' },
                        title: { display: true, text: flag ? Warehouse.Route : 'Sales of ' + Warehouse.Route },
                        tooltip: {
                            callbacks: {
                                label: function (context) {
                                    let label = context.dataset.label || '';
                                    if (label) { label += ':'; }
                                    label += NumberFormat(pricesFormat(parseFloat(context.parsed.y.toString().replace(/,/g, '.'))));
                                    label += "%"
                                    return label;
                                },
                            },
                        },
                    },
                    scales: flag ? {
                        y: { ticks: { callback: function (value) { return '$' + (pricesFormat(value)); } } }
                    } : {
                        y: { ticks: { callback: function (value) { return (Number(value).toFixed(0)) + "%"; } } }
                    }
                },
                data: {
                    labels: labels,
                    datasets: [{ label: 'Sales', spanGaps: true, data: data2, backgroundColor: LineColor, borderColor: LineColor }]
                }
            }
        }

        return result.data
    }
    return []
}

export async function getSalesPerSalesPerson(SalesPerson, DateC) {
    let temp = new Date()
    let Start = new Date(temp.getFullYear(), temp.getMonth(), 1)
    let End = new Date(temp.getFullYear(), temp.getMonth() + 1, 0)

    if (DateC) {
        Start = new Date(DateC.getFullYear(), DateC.getMonth(), 1)
        End = new Date(DateC.getFullYear(), DateC.getMonth() + 1, 0)
    }

    const data = new GeneralFormatToSend(getValueCookie('CompanyId'), SalesPerson ? SalesPerson : getValueCookie('SalesPerson'), Start.toISOString().split('T')[0], End.toISOString().split('T')[0], (Start.getMonth() + 1))

    let result = await fetchTotalSalesByRoute(SalesPerson, Start, End)
    const result2 = await getInformationWithData('/reports/getMetaGroupBySalesPersonV2', data)

    let TotalMeta = 0
    let GeneralTotal = 0
    result.data.forEach(element => {
        GeneralTotal += Number(element.NonTaxableSalesAmt)
    })
    result2.data.forEach(element => {
        TotalMeta += Number(element.Meta)
    });
    let processInfo = []
    let Months = new TitlesSalesPerRoutes(false, (Start.getMonth() + 1), "SP")

    if (result.status.code === 1 && result2.status.code === 1) {
        result.data.forEach(row => {
            const indexMeta = getIndexElement(result2.data, 'Ruta', row.SalespersonNo)
            const index = getIndexElement(processInfo, 'Name', row.SalespersonNo)
            // TransactionDate, not InvoiceDate (2026-09-09) — see fetchTotalSalesByRoute's header comment above.
            const date = row.TransactionDate ? row.TransactionDate.split('-') : [];
            if (date.length === 3) {
                const index2 = getIndexElement(Months, 'Month', MonthInLetters(date[1]))
                let total = 0
                if (index2 !== -1) {
                    Months[index2].Total += (Number(row.NonTaxableSalesAmt))
                    Months[index2].TotalMeta = TotalMeta
                    total = Months[index2].Total
                }

                let to = 0
                if (index !== -1) {
                    to = processInfo[index].addData(MonthInLetters(date[1]), row.NonTaxableSalesAmt, indexMeta !== -1 ? result2.data[indexMeta].Meta : 0, "SP")
                    processInfo[index].addRow(row)
                } else {
                    let newRoute = new SalesPerRouteMonth(row.SalespersonNo, (Start.getMonth() + 1), "SP")
                    newRoute.addRow(row)
                    to = newRoute.addData(MonthInLetters(date[1]), row.NonTaxableSalesAmt, (indexMeta !== -1 ? result2.data[indexMeta].Meta : 0), "SP")
                    processInfo.push(newRoute)
                }

                for (const route of processInfo) {
                    const indexMonth = getIndexElement(route.Data, 'Month', MonthInLetters(date[1]))
                    if (indexMonth !== -1 && to > 0) {
                        route.Data[indexMonth].Percentage = Number(total) > 0 ? ((Number(route.Data[indexMonth].Total) / Number(total)) * 100) : 0
                        route.Data[indexMonth].PercentageMeta = TotalMeta > 0 ? ((Number(route.Data[indexMonth].TotalMeta) / TotalMeta) * 100) : 0
                        route.Data[indexMonth].PercentageComplete = Number(route.Data[indexMonth].TotalMeta) > 0 ? ((Number(route.Data[indexMonth].Total)) / (Number(route.Data[indexMonth].TotalMeta))) * 100 : 0
                        if (route.Data[indexMonth].PercentageComplete < 30) {
                            route.Color = 'grayTableCell'
                        } else if (route.Data[indexMonth].PercentageComplete < 99) {
                            route.Color = 'yellowTableCell'
                        } else {
                            route.Color = 'greenTableCell'
                        }
                    }
                }

                if (index2 !== -1) {
                    Months[index2].addRoute(row.SalespersonNo, GeneralTotal > 0 ? (to / GeneralTotal) * 100 : 0, row.SalespersonNo, random_rgba());
                }
            }
        });
    }

    processInfo = processInfo.sort((a, b) => {
        return a.Data[0].Total < b.Data[0].Total ? 1 : a.Data[0].Total > b.Data[0].Total ? -1 : 0;
    });
    return { Information: processInfo, Months: Months }
}

export async function getCustomerExcess(SalesPerson) {
    // carteraClientesExcesoV2 verified 2026-09-04 to already be idCompany-aware
    // (alt__mas90_mirror__AR_Customer) — reused unchanged.
    const data = new GeneralFormatToSend(getValueCookie('CompanyId'), SalesPerson ? SalesPerson : getValueCookie('SalesPerson'))
    const result = await getInformationWithData('/customer/get/CarteraV2', data)
    if (result.status.code === 1) {
        return result.data
    } else {
        return []
    }
}

export async function getCurrentBalance(SalesPerson) {
    // getCustomerBalanceV2 verified 2026-09-04 to already be idCompany-aware
    // (alt__mas90_mirror__AR_OpenInvoice/AR_Customer, both branches) — reused unchanged.
    const data = new GeneralFormatToSend(getValueCookie('CompanyId'), SalesPerson ? SalesPerson : getValueCookie('SalesPerson'))
    const result = await getInformationWithData('/customer/getBalanceV2', data)
    if (result.status.code === 1) {
        let actualDate = ActualDate()
        let info = []
        for (const invoice of result.data) {
            if (intervalTimeInDays2(actualDate, DateFormatMAS90(invoice.InvoiceDueDate)) <= 0) {
                invoice.DaysDlq = intervalTimeInDays(invoice.InvoiceDueDate, getActualDateUTC())
                info.push(invoice)
            }
        }
        return info
    } else {
        return []
    }
}

export async function getCustomerHistory(SalesPerson) {
    // HistoryV2 verified 2026-09-04 to already be idCompany-aware
    // (alt__mas90_mirror__AR_Customer) — reused unchanged.
    const data = new GeneralFormatToSend(getValueCookie('CompanyId'), SalesPerson ? SalesPerson : getValueCookie('SalesPerson'))
    const result = await getInformationWithData('/user/HistoryV2', data)
    const result2 = await getInformationWithData('/user/NotesLastMonthV2', data)

    if (!result || result.status.code !== 1) return []
    const customers = result.data || []
    for (const customer of customers) {
        const index = getIndexElement(result2 && result2.data ? result2.data : [], 'CustomerNo', customer.CustomerNo)
        customer.NumberDays = customer.DateLastActivity ? NumberFormat(Number(intervalTimeInDays(customer.DateLastActivity, getActualDateUTC())) - 1) : "Never"
        customer.HaveNotes = index !== -1
    }
    return customers
}

export async function getSalesByCustomer(SalesPerson, DateC) {
    let temp = new Date()
    let Start = new Date(temp.getFullYear(), temp.getMonth(), 1)
    let End = new Date(temp.getFullYear(), temp.getMonth() + 1, 0)

    if (DateC) {
        Start = new Date(DateC.getFullYear(), DateC.getMonth(), 1)
        End = new Date(DateC.getFullYear(), DateC.getMonth() + 1, 0)
    }

    const result = await fetchTotalSalesByRoute(SalesPerson, Start, End)
    if (result.status.code === 1) {
        let info = new SalesByCustomerRoute()
        for (const Customer of result.data) {
            info.addCustomer(Customer.CustomerNo, Customer.BillToName, Customer.NonTaxableSalesAmt, Customer.UDF_RUTA, Customer)
        }
        info.finalize()
        return info.Data
    }
    return []
}

export async function getSalesPerMonth(SalesPerson, DateC) {
    let actualDate = new Date()
    let Start = new Date(actualDate.getFullYear(), actualDate.getMonth(), 1)
    let End = new Date(actualDate.getFullYear(), actualDate.getMonth() + 1, 0)

    if (DateC) {
        Start = new Date(DateC.getFullYear(), DateC.getMonth(), 1)
        End = new Date(DateC.getFullYear(), DateC.getMonth() + 1, 0)
    }

    const idCompany = currentIdCompany();
    // '-txn' (TransactionDate-grouped) variant (2026-09-09) — see
    // fetchTotalSalesByRoute's header comment above for the root-cause writeup.
    const v3Result = await getInformationWithDataV3('/v3/sales/actual-month-txn', {
        idCompany,
        SalespersonNo: SalesPerson ? SalesPerson : getValueCookie('SalesPerson'),
        Start: Start.toISOString().split('T')[0],
        End: End.toISOString().split('T')[0],
    });
    const rows = (v3Result && v3Result.status && v3Result.status.code === 1 && Array.isArray(v3Result.data)) ? v3Result.data : [];
    const byDay = {};
    for (const row of rows) {
        // TransactionDate, not InvoiceDate (2026-09-09) — see fetchTotalSalesByRoute's header comment above.
        const day = String(row.TransactionDate).slice(8, 10); // 'YYYY-MM-DD' -> 'DD'
        if (!byDay[day]) {
            byDay[day] = { NonTaxableSalesAmt: { total: 0 }, registros: 0, detail: [] };
        }
        byDay[day].NonTaxableSalesAmt.total += Number(row.NonTaxableSalesAmt) || 0;
        byDay[day].registros += 1;
        byDay[day].detail.push(row);
    }
    const result = byDay;

    if (!result || typeof result !== 'object' || Array.isArray(result)) return []

    const keys = Object.keys(result).filter(k => !isNaN(Number(k)))
    let processData = []
    for (const key of keys) {
        const dayData = result[key] || {};
        const salesAmt = dayData.NonTaxableSalesAmt || { total: 0 };
        const total = Number(salesAmt.total) || 0;
        const registros = dayData.registros || 0;
        const detail = dayData.detail || [];

        processData.push(new SalesPerMonth(Number(key), total, registros, detail))
        let week = getWeekNumber(new Date((Start.getMonth() + 1) + "/" + key + "/" + Start.getFullYear()))
        const index = getIndexElement(processData, 'Week', String(week))
        if (index !== -1) {
            processData[index].addInfo(total, registros, detail)
        } else {
            let newWeek = new SalesPerWeek(week)
            newWeek.addInfo(total, registros, detail)
            processData.push(newWeek)
        }
    }

    return processData
}
